// Story 8.8 (AC4, seam-82) — the case log's video source: fetches the Story 8.2
// pre-signed URL read (GET /calibration/cases/{case_id}) and CACHES the URL until
// `expires_in` — never beyond it, never a re-mint per mount. Every fetch writes a
// `calibration.case.fetched` audit row server-side (PR #538 review), so the cache
// window IS the audit-volume contract: one mint per case per TTL, re-fetch only
// after expiry (or an explicit refresh). retry: false — a 503 CAL-503 (#430
// unprovisioned) renders the calm "Case video unavailable" Banner, never a retry
// storm.

import { useQuery, type Query } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import type { CalibrationCaseVideoData } from "@/hooks/queries/calibration-log-types";

export const CALIBRATION_CASE_VIDEO_QUERY_KEY = "calibration-case-video";

/**
 * Safety margin subtracted from the server's `expires_in` (Story 8.10). The
 * browser clock and the signing clock are independent, and the window's tail is
 * the dangerous part: served at `expires_in - 1s`, a URL is fresh by our
 * accounting and already dead at S3. Re-minting a few seconds early costs one
 * extra audit row per case per window; serving a dead URL costs the clinician a
 * broken player mid-case.
 */
export const VIDEO_URL_SKEW_MARGIN_MS = 45_000;

/**
 * Freshness window for a fetched pre-signed URL, in ms: the server's `expires_in`
 * minus the skew margin (seam-82 — cache UNTIL expiry, never beyond). Exported
 * pure so the contract is unit-testable; a missing/invalid payload is immediately
 * stale (defensive 0 — the next mount re-fetches rather than serving a broken
 * URL), and so is any window shorter than the margin itself.
 *
 * `Number.isFinite` is load-bearing, not decoration: `typeof NaN === "number"`
 * and `NaN <= 0` is false, so a NaN `expires_in` would otherwise fall straight
 * through and return NaN as a staleTime.
 */
export function videoUrlStaleTimeMs(data: CalibrationCaseVideoData | undefined): number {
  if (!data || !Number.isFinite(data.expires_in) || data.expires_in <= 0) return 0;
  return Math.max(0, data.expires_in * 1000 - VIDEO_URL_SKEW_MARGIN_MS);
}

/**
 * How long an UNMOUNTED video entry survives in the cache. gcTime has no
 * response-derived function form, so this is a fixed ceiling that must stay
 * ≥ any configured presign TTL (CALIBRATION_PRESIGN_TTL_SECONDS, default 900s) —
 * otherwise a log → board → log round trip after the default 5-min gc would
 * re-mint inside the validity window and inflate the audit volume.
 */
export const VIDEO_URL_GC_TIME_MS = 60 * 60 * 1000;

/** Exported so the seam-82 wiring (not just the pure helper) is unit-pinnable. */
export function calibrationCaseVideoQueryOptions(caseId: string | undefined) {
  return {
    queryKey: [CALIBRATION_CASE_VIDEO_QUERY_KEY, caseId],
    queryFn: () =>
      apiFetch<CalibrationCaseVideoData>(
        `/calibration/cases/${encodeURIComponent(caseId ?? "")}`,
      ),
    enabled: Boolean(caseId),
    // TanStack v5 function form: staleTime is derived from the RESPONSE, so the
    // cached URL stays fresh exactly as long as the signature is valid.
    staleTime: (query: Query<CalibrationCaseVideoData>) =>
      videoUrlStaleTimeMs(query.state.data),
    gcTime: VIDEO_URL_GC_TIME_MS,
    retry: false as const,
  };
}

export function useCalibrationCaseVideo(caseId: string | undefined) {
  return useQuery(calibrationCaseVideoQueryOptions(caseId));
}
